import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import {
  Timer,
  Download,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Info,
  Trophy,
  Users,
  Link2,
  ArrowLeft,
  MapPin,
  Radio,
  Camera,
  Zap,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { API } from '../config/api';

/**
 * Speedhive Importer (Cronometraje)
 *
 * Two entry points, both landing on the same "event tree → import" flow:
 *   1. Category browser (default): admin-configured categories with an
 *      `org_id` populate a dropdown; picking one lists that organization's
 *      recent motorsport events.
 *   2. Direct URL/ID input (fallback): power user pastes any Speedhive
 *      event link and jumps straight to the event tree.
 *
 * Data imported here is shared across all Pro users (see backend rationale).
 */
const SpeedhiveImporter = ({ onImportSuccess }) => {
  // Categories + org-events browser state
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [orgEvents, setOrgEvents] = useState([]);
  const [loadingOrgEvents, setLoadingOrgEvents] = useState(false);

  // Direct URL fallback
  const [rawInput, setRawInput] = useState('');
  const [showDirect, setShowDirect] = useState(false);

  // Event tree state (shared by both flows)
  const [loadingEvent, setLoadingEvent] = useState(false);
  const [event, setEvent] = useState(null);

  // Import state (session-scoped)
  const [importing, setImporting] = useState(null);
  const [conflict, setConflict] = useState(null);

  // Load categories on mount.
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/speedhive/categories`);
        setCategories(res.data || []);
      } catch (err) {
        toast.error('No pude cargar la lista de categorías');
      }
    };
    load();
  }, []);

  const activeCategory = useMemo(
    () => categories.find((c) => c.id === selectedCategoryId) || null,
    [categories, selectedCategoryId],
  );

  // When a category is picked, fetch its organization's events.
  useEffect(() => {
    if (!activeCategory?.org_id) {
      setOrgEvents([]);
      return;
    }
    const load = async () => {
      setLoadingOrgEvents(true);
      setOrgEvents([]);
      setEvent(null);
      try {
        const res = await axios.get(
          `${API}/speedhive/organizations/${activeCategory.org_id}/events`,
          { params: { count: 25, offset: 0 } },
        );
        setOrgEvents(res.data || []);
      } catch (err) {
        toast.error(
          err.response?.data?.detail || 'No pude traer los eventos de Speedhive',
        );
      } finally {
        setLoadingOrgEvents(false);
      }
    };
    load();
  }, [activeCategory]);

  const loadEventById = async (eventId) => {
    setLoadingEvent(true);
    setEvent(null);
    try {
      const res = await axios.get(`${API}/speedhive/event/${eventId}`);
      setEvent(res.data);
    } catch (err) {
      toast.error(
        err.response?.data?.detail || 'Speedhive no respondió. Reintentá.',
      );
    } finally {
      setLoadingEvent(false);
    }
  };

  const loadEventFromRaw = async () => {
    const trimmed = rawInput.trim();
    if (!trimmed) {
      toast.error('Pegá un link o ID de evento de Speedhive');
      return;
    }
    setLoadingEvent(true);
    setEvent(null);
    try {
      const parseRes = await axios.get(`${API}/speedhive/parse-url`, {
        params: { url: trimmed },
      });
      const eventId = parseRes.data.event_id;
      if (!eventId) {
        toast.error(
          'No pude identificar el ID del evento. Pegá el link de Speedhive, por ejemplo speedhive.mylaps.com/events/3518475',
        );
        return;
      }
      const res = await axios.get(`${API}/speedhive/event/${eventId}`);
      setEvent(res.data);
    } catch (err) {
      toast.error(
        err.response?.data?.detail || 'Speedhive no respondió. Reintentá.',
      );
    } finally {
      setLoadingEvent(false);
    }
  };

  const doImport = async (session, mode) => {
    if (!event) return;
    setImporting(session.id);
    try {
      const res = await axios.post(`${API}/speedhive/import-session`, {
        event_id: event.id,
        session_id: session.id,
        mode,
      });
      toast.success(
        `✓ ${res.data.action === 'replaced' ? 'Reemplazada' : 'Importada'}: ` +
          `${res.data.driver_count} pilotos · ${res.data.lap_count} vueltas`,
      );
      const refreshed = await axios.get(`${API}/speedhive/event/${event.id}`);
      setEvent(refreshed.data);
      if (onImportSuccess) onImportSuccess(res.data);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409) {
        setConflict({ session, message: detail });
      } else {
        toast.error(detail || 'Error al importar');
      }
    } finally {
      setImporting(null);
    }
  };

  const confirmReplace = async (mode) => {
    if (!conflict) return;
    const session = conflict.session;
    setConflict(null);
    await doImport(session, mode);
  };

  const backToEventList = () => {
    setEvent(null);
  };

  // Header stats for the event tree.
  const stats = useMemo(() => {
    if (!event) return null;
    let total = 0;
    let official = 0;
    let imported = 0;
    for (const g of event.groups || []) {
      for (const s of g.sessions || []) {
        total += 1;
        if (s.is_official) official += 1;
        if (s.already_imported) imported += 1;
      }
    }
    return { total, official, imported };
  }, [event]);

  const speedhiveCategories = categories.filter((c) => !!c.org_id);

  // ---------------------------------------------------------------------------
  // "En vivo" mode: browse currently-live events + snapshot/capture flow.
  // ---------------------------------------------------------------------------
  const [mode, setMode] = useState('event'); // 'event' | 'live'
  const [liveCountry, setLiveCountry] = useState('AR');
  const [liveEvents, setLiveEvents] = useState([]);
  const [loadingLive, setLoadingLive] = useState(false);
  const [liveSelected, setLiveSelected] = useState(null); // one live event
  const [liveSnapshot, setLiveSnapshot] = useState(null);
  const [loadingSnap, setLoadingSnap] = useState(false);
  const [snapshotImporting, setSnapshotImporting] = useState(false);
  // Map: live_event_id → true when a category has this id configured
  // (i.e. background auto-capture will handle it — no manual buttons needed).
  const autoConfiguredIds = useMemo(() => {
    const s = new Set();
    for (const c of categories) {
      const id = (c.live_event_id || '').trim();
      if (id && c.is_active !== false) s.add(id);
    }
    return s;
  }, [categories]);

  const loadLiveEvents = async () => {
    setLoadingLive(true);
    try {
      const params = liveCountry ? { country: liveCountry } : {};
      const res = await axios.get(`${API}/speedhive/live/events`, { params });
      setLiveEvents(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'lt-api no respondió');
    } finally {
      setLoadingLive(false);
    }
  };

  // Auto-refresh live events every 30s while on the live tab.
  useEffect(() => {
    if (mode !== 'live') return;
    loadLiveEvents();
    const id = setInterval(loadLiveEvents, 30000);
    return () => clearInterval(id);
  }, [mode, liveCountry]);

  const loadSnapshot = async (leid) => {
    setLoadingSnap(true);
    setLiveSnapshot(null);
    try {
      const res = await axios.get(`${API}/speedhive/live/events/${leid}/snapshot`);
      setLiveSnapshot(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error trayendo snapshot');
    } finally {
      setLoadingSnap(false);
    }
  };

  const captureSnapshotOnce = async () => {
    if (!liveSelected) return;
    setSnapshotImporting(true);
    try {
      const res = await axios.post(`${API}/speedhive/live/import-snapshot`, {
        live_event_id: liveSelected.live_event_id,
      });
      toast.success(
        `✓ Snapshot guardado: ${res.data.driver_count} pilotos · ${res.data.lap_count_with_sectors} vueltas con parciales`,
      );
      if (onImportSuccess) onImportSuccess(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar snapshot');
    } finally {
      setSnapshotImporting(false);
    }
  };

  return (
    <Card
      className="bg-neutral-900 border-red-900/30"
      data-testid="speedhive-importer"
    >
      <CardHeader>
        <CardTitle className="text-xl text-white flex items-center gap-2">
          <Timer className="w-5 h-5 text-red-500" />
          Cronometraje · Speedhive
        </CardTitle>
        <CardDescription className="text-gray-400">
          Elegí una categoría configurada por el admin para ver sus eventos
          recientes, o pegá el link de{' '}
          <a
            href="https://speedhive.mylaps.com"
            target="_blank"
            rel="noreferrer"
            className="text-red-400 hover:text-red-300 inline-flex items-center gap-1"
          >
            speedhive.mylaps.com
            <ExternalLink className="w-3 h-3" />
          </a>
          . Podés importar sesiones{' '}
          <Badge className="mx-1 bg-green-600/20 text-green-400 text-[10px] px-1.5 py-0">
            Oficial
          </Badge>{' '}
          o{' '}
          <Badge className="mx-1 bg-amber-600/20 text-amber-300 text-[10px] px-1.5 py-0">
            Provisional
          </Badge>{' '}
          (con aviso de retiming), o capturar en vivo con parciales reales.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Mode selector: post-session vs live */}
        <div className="inline-flex items-center gap-1 rounded-lg border border-neutral-800 bg-neutral-900/60 p-1">
          <button
            onClick={() => setMode('event')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              mode === 'event'
                ? 'bg-red-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
            data-testid="speedhive-mode-event"
          >
            Post-sesión
          </button>
          <button
            onClick={() => setMode('live')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
              mode === 'live'
                ? 'bg-red-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
            data-testid="speedhive-mode-live"
          >
            <Radio className="w-3.5 h-3.5" />
            En Vivo
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
          </button>
        </div>

        {/* Live mode content */}
        {mode === 'live' && (
          <LivePanel
            liveEvents={liveEvents}
            loadingLive={loadingLive}
            liveCountry={liveCountry}
            setLiveCountry={setLiveCountry}
            liveSelected={liveSelected}
            setLiveSelected={(e) => {
              setLiveSelected(e);
              if (e) loadSnapshot(e.live_event_id);
            }}
            liveSnapshot={liveSnapshot}
            loadingSnap={loadingSnap}
            reloadSnapshot={() =>
              liveSelected && loadSnapshot(liveSelected.live_event_id)
            }
            snapshotImporting={snapshotImporting}
            captureSnapshotOnce={captureSnapshotOnce}
            autoConfiguredIds={autoConfiguredIds}
          />
        )}

        {/* Post-session mode: category dropdown + fallback URL */}
        {mode === 'event' && !event && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
                Categoría
              </label>
              <div className="flex-1 min-w-[220px]">
                <Select
                  value={selectedCategoryId}
                  onValueChange={setSelectedCategoryId}
                >
                  <SelectTrigger
                    className="bg-neutral-800 border-neutral-700 text-white"
                    data-testid="speedhive-category-select"
                  >
                    <SelectValue placeholder="Elegí una categoría…" />
                  </SelectTrigger>
                  <SelectContent className="bg-neutral-900 border-neutral-700 text-white">
                    {speedhiveCategories.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500 italic">
                        No hay categorías configuradas con Speedhive todavía.
                      </div>
                    ) : (
                      speedhiveCategories.map((c) => (
                        <SelectItem
                          key={c.id}
                          value={c.id}
                          className="focus:bg-red-950/40"
                          data-testid={`speedhive-category-opt-${c.id}`}
                        >
                          <div className="flex flex-col items-start">
                            <span className="text-sm">{c.name}</span>
                            {c.organizer_name && (
                              <span className="text-[10px] text-gray-500">
                                {c.organizer_name}
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <button
                onClick={() => setShowDirect((v) => !v)}
                className="text-xs text-gray-400 hover:text-red-400 inline-flex items-center gap-1"
                data-testid="speedhive-toggle-direct"
              >
                <Link2 className="w-3 h-3" />
                {showDirect ? 'Ocultar link directo' : 'Pegar link directo'}
              </button>
            </div>

            {/* Direct URL fallback (collapsible) */}
            {showDirect && (
              <div className="rounded-lg border border-neutral-800 bg-neutral-900/40 p-3 space-y-2">
                <p className="text-xs text-gray-500">
                  Si conocés el link exacto de Speedhive, pegalo acá y saltás la
                  navegación por categoría.
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <Input
                    value={rawInput}
                    onChange={(e) => setRawInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !loadingEvent) loadEventFromRaw();
                    }}
                    placeholder="https://speedhive.mylaps.com/events/3518475"
                    className="flex-1 min-w-[240px] bg-neutral-800 border-neutral-700 text-white placeholder:text-gray-500"
                    data-testid="speedhive-url-input"
                  />
                  <Button
                    onClick={loadEventFromRaw}
                    disabled={loadingEvent}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="speedhive-load-btn"
                  >
                    {loadingEvent ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                        Buscando…
                      </>
                    ) : (
                      <>Abrir evento</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* --- Org events list --- */}
        {mode === 'event' && !event && activeCategory && (
          <div
            className="rounded-lg border border-neutral-800 bg-neutral-900/30 overflow-hidden"
            data-testid="speedhive-org-events"
          >
            <div className="px-4 py-2 bg-neutral-900/70 border-b border-neutral-800 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-white uppercase tracking-wide">
                  Eventos recientes · {activeCategory.name}
                </h3>
                {activeCategory.organizer_name && (
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    Organizador Speedhive: {activeCategory.organizer_name}
                  </p>
                )}
              </div>
              {loadingOrgEvents && (
                <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
              )}
            </div>
            {loadingOrgEvents ? (
              <div className="py-8 text-center text-sm text-gray-500">
                Cargando eventos…
              </div>
            ) : orgEvents.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-500">
                No hay eventos recientes para esta organización.
              </div>
            ) : (
              <div className="divide-y divide-neutral-800/70">
                {orgEvents.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => loadEventById(e.id)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-neutral-800/40 transition-colors text-left"
                    data-testid={`speedhive-org-event-${e.id}`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-red-600/20 flex items-center justify-center flex-shrink-0">
                      <Trophy className="w-4 h-4 text-red-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white truncate">
                        {e.name}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{e.start_date || ''}</span>
                        {e.location_name && (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {e.location_name}
                          </span>
                        )}
                        {e.location_length_label && (
                          <span className="text-gray-600">
                            {e.location_length_label}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-[10px] text-gray-500">#{e.id}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Empty state when nothing picked */}
        {mode === 'event' && !event && !activeCategory && !loadingEvent && !showDirect && (
          <div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-6 text-center">
            <Info className="w-8 h-8 mx-auto mb-2 text-gray-500" />
            <p className="text-sm text-gray-400 leading-relaxed">
              Elegí una categoría del desplegable para ver los eventos más
              recientes, o usá el link directo si ya tenés uno a mano.
            </p>
          </div>
        )}

        {/* --- Event tree (drilled-in view) --- */}
        {mode === 'event' && loadingEvent && (
          <div className="py-8 text-center text-gray-400">
            <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" />
            <span className="text-sm">Cargando evento…</span>
          </div>
        )}
        {mode === 'event' && event && stats && (
          <div className="space-y-4" data-testid="speedhive-event-tree">
            {/* Back + header */}
            <div className="flex items-center gap-3">
              <button
                onClick={backToEventList}
                className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors"
                data-testid="speedhive-back-btn"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Volver
              </button>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-base truncate">
                  {event.name}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Speedhive event #{event.id}
                </p>
              </div>
              <div className="text-xs text-gray-500 whitespace-nowrap hidden sm:block">
                <span className="inline-flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" />
                  {event.groups.length} categorías
                </span>
                <span className="mx-2">·</span>
                <span>
                  {stats.total} sesiones · {stats.official} oficiales
                  {stats.imported > 0 && (
                    <span className="ml-1 text-green-400">
                      · {stats.imported} ya importadas
                    </span>
                  )}
                </span>
              </div>
            </div>

            {/* Groups (classes) */}
            {event.groups.map((group) => (
              <div
                key={group.id}
                className="rounded-lg border border-neutral-800 bg-neutral-900/30 overflow-hidden"
                data-testid={`speedhive-group-${group.id}`}
              >
                <div className="px-4 py-2 bg-neutral-900/70 border-b border-neutral-800">
                  <h4 className="text-sm font-semibold text-white uppercase tracking-wide">
                    {group.name}
                  </h4>
                </div>
                <div className="divide-y divide-neutral-800/70">
                  {group.sessions
                    .filter(
                      (s) => s.start_time && !s.start_time.startsWith('1970-'),
                    )
                    .map((s) => (
                      <SessionRow
                        key={s.id}
                        session={s}
                        importing={importing === s.id}
                        onImport={() => doImport(s, 'create')}
                      />
                    ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Conflict modal */}
      <Dialog
        open={!!conflict}
        onOpenChange={(open) => !open && setConflict(null)}
      >
        <DialogContent
          className="bg-neutral-900 border-red-900/30 text-white"
          data-testid="speedhive-conflict-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Sesión ya importada
            </DialogTitle>
            <DialogDescription className="text-gray-400 pt-2">
              {conflict?.message}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-neutral-800/60 p-3 text-sm text-gray-300 space-y-1">
            <p>
              <b className="text-white">Sesión:</b> {conflict?.session.name}
            </p>
            <p className="text-xs text-gray-500">
              Speedhive session #{conflict?.session.id}
            </p>
          </div>
          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setConflict(null)}
              className="border-neutral-700 text-gray-200 hover:bg-neutral-800"
              data-testid="speedhive-conflict-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => confirmReplace('force')}
              variant="outline"
              className="border-amber-700 text-amber-300 hover:bg-amber-950/40"
              data-testid="speedhive-conflict-force"
            >
              Reemplazar de todas formas
            </Button>
            <Button
              onClick={() => confirmReplace('replace')}
              className="bg-red-600 hover:bg-red-700"
              data-testid="speedhive-conflict-replace"
            >
              Reemplazar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Live capture panel (currently-running Speedhive events).
// ---------------------------------------------------------------------------
const LivePanel = ({
  liveEvents,
  loadingLive,
  liveCountry,
  setLiveCountry,
  liveSelected,
  setLiveSelected,
  liveSnapshot,
  loadingSnap,
  reloadSnapshot,
  snapshotImporting,
  captureSnapshotOnce,
  autoConfiguredIds,
}) => {
  const isAutoManaged =
    liveSelected && autoConfiguredIds?.has?.(liveSelected.live_event_id);

  return (
    <div className="space-y-3" data-testid="speedhive-live-panel">
      {/* Country filter + refresh */}
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
          País
        </label>
        <div className="flex items-center gap-1 rounded-md border border-neutral-800 bg-neutral-900/60 p-0.5">
          {['AR', 'BR', 'ALL'].map((cc) => (
            <button
              key={cc}
              onClick={() => setLiveCountry(cc === 'ALL' ? '' : cc)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                (liveCountry || 'ALL') === (cc === 'ALL' ? '' : cc) ||
                (cc === 'ALL' && !liveCountry)
                  ? 'bg-neutral-700 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
              data-testid={`live-country-${cc}`}
            >
              {cc}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <span className="text-xs text-gray-500">
          Se refresca cada 30s.{' '}
          {loadingLive ? <Loader2 className="w-3 h-3 inline animate-spin" /> : null}
        </span>
      </div>

      {/* Auto-capture banner */}
      <div
        className="rounded-md border border-red-900/40 bg-red-950/20 px-3 py-2 flex items-start gap-2 text-xs text-gray-300"
        data-testid="speedhive-auto-info"
      >
        <Zap className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
        <div className="leading-relaxed">
          <b className="text-white">Captura automática:</b> los eventos cuya
          categoría tenga un <b>Live Event ID</b> configurado en el panel de
          admin se capturan solos, sesión por sesión, con parciales reales —
          sin apretar nada. Este panel es solo para <b>vista previa</b> y para
          capturar snapshots puntuales de eventos que no tengan captura
          automática activa.
        </div>
      </div>

      {/* Events list */}
      {!liveSelected && (
        <div
          className="rounded-lg border border-neutral-800 bg-neutral-900/30 overflow-hidden"
          data-testid="speedhive-live-events"
        >
          {liveEvents.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">
              No hay eventos en vivo{liveCountry ? ` en ${liveCountry}` : ''} ahora mismo.
            </div>
          ) : (
            <div className="divide-y divide-neutral-800/70">
              {liveEvents.map((e) => {
                const auto = autoConfiguredIds?.has?.(e.live_event_id);
                return (
                  <button
                    key={e.live_event_id}
                    onClick={() => setLiveSelected(e)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-neutral-800/40 transition-colors text-left"
                    data-testid={`live-event-${e.live_event_id}`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-red-600/20 flex items-center justify-center flex-shrink-0 relative">
                      <Radio className="w-4 h-4 text-red-400" />
                      <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white truncate">
                        {e.name}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 inline-flex items-center gap-2 flex-wrap">
                        {e.country_code && <span>[{e.country_code}]</span>}
                        {e.track_name && (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {e.track_name}
                          </span>
                        )}
                        {e.track_length_km && (
                          <span className="text-gray-600">
                            {e.track_length_km.toFixed(2)} km
                          </span>
                        )}
                      </div>
                    </div>
                    {auto && (
                      <Badge className="bg-green-600/20 text-green-400 text-[10px] px-1.5 py-0 inline-flex items-center gap-1 mr-1">
                        <Zap className="w-3 h-3" />
                        AUTO
                      </Badge>
                    )}
                    <Badge className="bg-red-600/20 text-red-400 text-[10px] px-1.5 py-0">
                      LIVE
                    </Badge>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Snapshot detail */}
      {liveSelected && (
        <div
          className="rounded-lg border border-neutral-800 bg-neutral-900/30 overflow-hidden"
          data-testid="speedhive-live-snapshot"
        >
          <div className="px-4 py-3 bg-neutral-900/70 border-b border-neutral-800 flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setLiveSelected(null)}
              className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors"
              data-testid="live-back-btn"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Volver
            </button>
            <div className="flex-1 min-w-0">
              <h4 className="text-white font-semibold text-sm truncate">
                {liveSelected.name}
              </h4>
              <p className="text-[11px] text-gray-500 mt-0.5">
                {liveSnapshot?.session_name || '—'} · {liveSnapshot?.group_name || ''}
              </p>
            </div>
            <button
              onClick={reloadSnapshot}
              disabled={loadingSnap}
              className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1"
              data-testid="live-refresh-snapshot"
            >
              {loadingSnap ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                'Refrescar'
              )}
            </button>
          </div>

          {/* Preview: top 5 with sectors */}
          {liveSnapshot && (
            <div className="p-4 space-y-2">
              {isAutoManaged && (
                <div
                  className="rounded-md border border-green-900/40 bg-green-950/20 px-3 py-2 flex items-start gap-2 text-xs"
                  data-testid="live-auto-active"
                >
                  <div className="relative">
                    <Zap className="w-4 h-4 text-green-400" />
                    <span className="absolute -top-1 -right-1 w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  </div>
                  <div className="text-gray-200 leading-relaxed">
                    <b className="text-green-400">Captura automática activa.</b>{' '}
                    Este evento tiene una categoría con Live Event ID
                    configurado — el sistema está guardando cada sesión con
                    parciales reales sin intervención tuya.
                  </div>
                </div>
              )}

              <div className="text-xs text-gray-500">
                {liveSnapshot.participants.length} pilotos ·{' '}
                {liveSnapshot.participants.filter(
                  (p) => p.sector_times_s && p.sector_times_s.length > 0,
                ).length}{' '}
                con parciales visibles
              </div>
              <div className="rounded-md border border-neutral-800 overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-neutral-900/70 text-gray-500 text-left">
                      <th className="px-2 py-1.5">Pos</th>
                      <th className="px-2 py-1.5">Piloto</th>
                      <th className="px-2 py-1.5">Vueltas</th>
                      <th className="px-2 py-1.5">Mejor</th>
                      <th className="px-2 py-1.5">Sectores (última)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-800/70">
                    {liveSnapshot.participants.slice(0, 5).map((p) => (
                      <tr key={p.number} className="text-gray-200">
                        <td className="px-2 py-1.5">{p.position || '—'}</td>
                        <td className="px-2 py-1.5">
                          <span className="text-white">
                            #{p.number} {p.name}
                          </span>
                          {p.class_name && (
                            <span className="ml-1 text-gray-500">
                              · {p.class_name}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5">{p.laps}</td>
                        <td className="px-2 py-1.5 font-mono">
                          {p.best_time_s ? p.best_time_s.toFixed(3) : '—'}
                        </td>
                        <td className="px-2 py-1.5 font-mono text-gray-400">
                          {p.sector_times_s && p.sector_times_s.length
                            ? p.sector_times_s
                                .map((s) => s.toFixed(3))
                                .join(' · ')
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Snapshot-only action (kept for on-demand grabs of events
                  that DON'T have auto-capture configured). */}
              {!isAutoManaged && (
                <div className="flex items-center gap-2 flex-wrap pt-1">
                  <Button
                    size="sm"
                    onClick={captureSnapshotOnce}
                    disabled={
                      snapshotImporting || !liveSnapshot.participants.length
                    }
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="live-snapshot-import-btn"
                    title="Guarda la foto actual como una sesión de carrera"
                  >
                    {snapshotImporting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        Guardando…
                      </>
                    ) : (
                      <>
                        <Camera className="w-4 h-4 mr-1" />
                        Capturar snapshot ahora
                      </>
                    )}
                  </Button>
                  <span className="text-[11px] text-gray-500 leading-snug">
                    Este evento no está en captura automática. Podés guardar
                    una foto puntual, o configurar el Live Event ID desde el
                    panel de admin para captura continua.
                  </span>
                </div>
              )}
            </div>
          )}
          {loadingSnap && !liveSnapshot && (
            <div className="py-8 text-center text-sm text-gray-500">
              <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" />
              Cargando snapshot…
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SessionRow = ({ session, importing, onImport }) => {
  const isOfficial = session.is_official;
  const isProvisional =
    (session.result_status || '').toLowerCase() === 'provisional';
  const alreadyImported = session.already_imported;
  // Both Official and Provisional are importable now — Provisional shows a
  // warning badge but the button is enabled.
  const canImport = isOfficial || isProvisional;

  const startLabel = (() => {
    if (!session.start_time) return '';
    const d = new Date(session.start_time);
    if (Number.isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm} · ${hh}:${mi}`;
  })();

  return (
    <div
      className="flex items-center gap-3 px-4 py-2.5 hover:bg-neutral-800/40 transition-colors"
      data-testid={`speedhive-session-${session.id}`}
    >
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
          session.type === 'race'
            ? 'bg-red-600/20 text-red-400'
            : session.type === 'qualify'
              ? 'bg-blue-600/20 text-blue-400'
              : 'bg-neutral-700/50 text-gray-400'
        }`}
      >
        <Trophy className="w-4 h-4" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-white truncate">
            {session.name}
          </span>
          {isOfficial && (
            <Badge className="bg-green-600/20 text-green-400 text-[10px] px-1.5 py-0">
              Oficial
            </Badge>
          )}
          {isProvisional && !isOfficial && (
            <Badge className="bg-amber-600/20 text-amber-300 text-[10px] px-1.5 py-0">
              Provisional
            </Badge>
          )}
          {alreadyImported && (
            <Badge className="bg-neutral-700/70 text-gray-200 text-[10px] px-1.5 py-0 inline-flex items-center gap-0.5">
              <CheckCircle2 className="w-3 h-3" />
              Ya importada
            </Badge>
          )}
        </div>
        <div className="text-xs text-gray-500 mt-0.5">
          {startLabel}
          {session.type ? ` · ${session.type}` : ''}
        </div>
      </div>

      {canImport ? (
        <Button
          size="sm"
          onClick={onImport}
          disabled={importing}
          title={
            isProvisional && !isOfficial
              ? 'Resultado provisional: puede tener retiming después'
              : 'Importar sesión oficial'
          }
          className={
            alreadyImported
              ? 'bg-neutral-700 hover:bg-neutral-600 text-gray-200'
              : isOfficial
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-amber-600 hover:bg-amber-700'
          }
          data-testid={`speedhive-import-btn-${session.id}`}
        >
          {importing ? (
            <>
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              Importando…
            </>
          ) : alreadyImported ? (
            <>
              <Download className="w-4 h-4 mr-1" />
              Reimportar
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-1" />
              Importar
            </>
          )}
        </Button>
      ) : (
        <span className="text-xs text-gray-500 italic">Sin datos</span>
      )}
    </div>
  );
};

export default SpeedhiveImporter;
